import React, { useCallback, useMemo, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router";
import UserFollowUpAnalytics from "../components/reviews/UserFollowUpAnalytics";
import UserFollowUpReports from "../components/reviews/UserFollowUpReports";
import {
  Container,
  EmptyState,
  ScreenContainer,
  H4,
  Link,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  ScreenHeader,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  TailSpin,
} from "../components/wombat-ui";
import useTranslations from "../hooks/useTranslations";
import { isUnauthorized } from "../utils/errors";

import { format } from "date-fns";
import {
  DotsVerticalIcon,
  InformationIcon,
  ReviewIcon,
  WarningIcon,
} from "../components/icons";
import UserFollowUpDocuments from "../components/reviews/UserFollowUpDocuments";
import UserFollowUpActivities from "../components/reviews/UserFollowUpActivities";
import {
  getLastReport,
  getOverdueReports,
  getReportStatus,
} from "../utils/reviewUtils";
import useFormatDate from "../hooks/useFormatDate";
import { NumberParam, useQueryParams } from "use-query-params";
import useNormalizedEvaluationOptions from "../hooks/useNormalizedEvaluationOptions";
import useNormalizedSkillOptions from "../hooks/useNormalizedSkillOptions";
import useNotification from "../hooks/useNotification";
import useConfirm from "../components/utils/confirm/useConfirm";
import CreateReportModal from "./CreateReportModal";
import { useBreadcrumbs } from "../hooks/useBreadcrumbs";
import { GetReviewsDocument, QuestionTypeEnum, ReviewFullFragment, useDeleteReview, useGetReviewFull } from '../graphql/generated/definitions';

type UserFollowUpOverdueAlertProps = {
  review: ReviewFullFragment;
};

const UserFollowUpOverdueAlert: React.FC<UserFollowUpOverdueAlertProps> = ({
  review,
}) => {
  const t = useTranslations();
  const overdues = useMemo(() => {
    return getOverdueReports(review);
  }, [review]);

  if (overdues.length === 0) return null;

  return (
    <div className="p-4 rounded-md bg-yellow-50">
      <div className="flex">
        <div className="flex-shrink-0">
          <WarningIcon className="w-5 h-5 text-warning-400" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-warning-800">
            {/* Check le pluriel */}
            {t("reviews.lateReport")}
          </h3>
          <div className="mt-2 text-sm text-warning-700">
            <ul className="pl-5 space-y-1 list-disc">
              {overdues.map(({ reason, concernedUsers, report }) => (
                <li key={report.id}>
                  {reason === "collaborator_overdue" &&
                    concernedUsers.length > 0 &&
                    t("reviews.reportOverdueCollaborator", {
                      collaborator: concernedUsers[0].name,
                      date: format(
                        new Date(report.createdAt),
                        t("dateFormat.short") as string
                      ),
                    })}
                  {reason === "referent_overdue" &&
                    concernedUsers.map((user) => user.name).join(", ")}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

type UserFollowUpStatusProps = {
  review: ReviewFullFragment;
};

const UserFollowUpStatus: React.FC<UserFollowUpStatusProps> = ({ review }) => {
  const t = useTranslations();
  const lastReport = useMemo(() => getLastReport(review), [review]);
  const status = useMemo(() => {
    const last = getLastReport(review);

    return getReportStatus(review, last);
  }, [review]);

  return (
    <div className="flex flex-col p-4 rounded-md bg-blue-50">
      <div className="flex">
        <div className="flex-shrink-0">
          <InformationIcon className="w-5 h-5 text-primary-300" />
        </div>
        <div className="flex-1 ml-3 md:flex md:justify-between">
          <p className="text-sm text-primary-700">
            {status === "awaiting_collaborator" &&
              t("reviews.waitingForCollaborator", {
                collaborator: review.collaborator.name,
              })}
            {status === "not_started" &&
              t("reviews.willStartOn", { date: review.startDate })}
            {status === "review_ended" &&
              t("reviews.reviewOverReportAvailable")}
            {status === "report_ready" && t("reviews.newReportAvailable")}
            {status === "awaiting_referents" &&
              t("reviews.waitingForReferent", {
                referent: `${lastReport.reportReferents
                  .filter((rr) => !rr.filledAt)
                  .map((r) => r.referent.name)
                  .join(", ")}`,
              })}
          </p>
          {review.lastCheckedReport && (
            <p className="mt-3 text-sm md:mt-0 md:ml-6">
              <Link
                to={`/reports/${review.lastCheckedReport.id}`}
                className="font-medium text-primary-700 whitespace-nowrap hover:text-primary-600"
              >
                {t("reviews.seeLastReport")}{" "}
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

type UserFollowUpBasicInformations = {
  review: ReviewFullFragment;
};

const UserFollowUpBasicInformations: React.FC<UserFollowUpBasicInformations> =
  ({ review }) => {
    const t = useTranslations();

    const formattedStartDate = useFormatDate({ date: review.startDate });
    const formattedEndDate = useFormatDate({ date: review.endDate });
    const formattedNextReportAt = useFormatDate({ date: review.nextReportAt });

    return (
      <div className="overflow-hidden bg-white">
        <H4>{t("reviews.followUpInfo")}</H4>
        <div className="py-5">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-light-900">
                {t("reviews.collaborator")}
              </dt>
              <dd className="mt-1 text-sm text-dark-100">
                <Link to={`/users/${review.collaborator.slug}`}>
                  {review.collaborator.name}
                </Link>
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-light-900">
                {t("reviews.startDate")}
              </dt>
              <dd className="mt-1 text-sm text-dark-100">
                {formattedStartDate}
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-light-900">
                {t("reviews.group")}
              </dt>
              <dd className="mt-1 text-sm text-dark-100">
                <Link to={`/campaigns/${review.campaign.slug}`}>
                  {review.campaign.name}
                </Link>
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-light-900">
                {t("reviews.endDate")}
              </dt>
              <dd className="mt-1 text-sm text-dark-100">{formattedEndDate}</dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-light-900">
                {t("reviews.referent", { count: review.referents.length })}
              </dt>
              <dd className="mt-1 text-sm text-dark-100">
                {review.referents.length > 0
                  ? review.referents.map((referent, index) => (
                      <React.Fragment key={referent.id}>
                        <Link to={`/users/${referent.slug}`} key={referent.id}>
                          {referent.name}
                        </Link>
                        {index < review.referents.length - 1 ? ", " : ""}
                      </React.Fragment>
                    ))
                  : t("reviews.noReferent")}
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-light-900">
                {t("reviews.nextReport")}
              </dt>
              <dd className="mt-1 text-sm text-dark-100">
                {review.nextReportAt
                  ? t("reviews.nextReportPlannedFor", {
                      date: formattedNextReportAt,
                    })
                  : t("reviews.noMoreReports")}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-light-900">
                {t("reviews.description")}
              </dt>
              <dd className="mt-1 text-sm text-dark-100">
                {review.description
                  ? review.description
                  : t("reviews.noDescription")}
              </dd>
            </div>
            <UserFollowUpDocuments review={review} />
          </dl>
        </div>
      </div>
    );
  };

function getQuestionCount(
  review: ReviewFullFragment,
  questionType: QuestionTypeEnum
) {
  return (
    review?.template?.sections.flatMap(({ questions }) =>
      questions.filter((question) => question.type === questionType)
    ).length || 0
  );
}

type UserFollowUpScreenProps = RouteComponentProps<{ id: string }>;

const UserFollowUpScreen: React.FC<UserFollowUpScreenProps> = ({ match }) => {
  const t = useTranslations();
  const history = useHistory();
  const { data, loading, error } = useGetReviewFull({
    variables: { id: match.params.id },
  });

  const confirm = useConfirm();
  const { notifySuccess } = useNotification();
  const [createReportModalOpen, setCreateReportModalOpen] = useState(false);

  const [{ tab = 0 }, setQuery] = useQueryParams({
    tab: NumberParam,
  });

  const handleTabChange = useCallback(
    (tab: number) => setQuery({ tab }, "replaceIn"),
    []
  );

  const review = data?.review;

  useBreadcrumbs([
    {
      to: "/campaigns",
      text: t("campaign.campaigns") as string,
    },
    {
      to: `/campaigns/${review?.campaign.id}`,
      text: review?.campaign.name,
    },
    {
      to: `/reviews/${review?.id}`,
      text: review?.title ? review?.title : review?.template?.title,
    },
  ]);

  const [deleteReview, { loading: deleteLoading }] = useDeleteReview({
    refetchQueries: [
      {
        query: GetReviewsDocument,
        variables: { sortBy: "createdAt", direction: "DESC" },
      },
    ],
  });

  const handleDeleteReview = useCallback(() => {
    confirm({
      title: t("reviews.delete"),
      message: t("reviews.deleteConfirm"),
      buttons: [
        { text: t("forms.actions.cancel") },
        {
          colorScheme: "red",
          text: t("forms.actions.delete"),
          id: "delete-review",
          onClick: async () => {
            await deleteReview({
              variables: { input: { id: data?.review?.id } },
            });
            notifySuccess({
              title: t(`reviews.notification.delete.title`) as string,
              description: t(`reviews.notification.delete.body`) as string,
            });
            history.push(`/campaigns/${review.campaign.slug}`);
          },
        },
      ],
    });
  }, [review]);

  const skillQuestionsCount = useMemo(
    () => getQuestionCount(review, "QuestionSkill"),
    [review]
  );
  const evaluationQuestionsCount = useMemo(
    () => getQuestionCount(review, "QuestionEvaluation"),
    [review]
  );

  const normalizedSkillptions = useNormalizedSkillOptions();
  const normalizedEvaluationOptions = useNormalizedEvaluationOptions();

  return (
    <ScreenContainer
      loading={loading}
      error={
        isUnauthorized(error) && (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link variant="button" to="/">
                {t("network.backToHome")}
              </Link>
            }
          />
        )
      }
    >
      {review && (
        <>
          <ScreenHeader
            title={review.template?.title}
            description={review.description}
            icon={ReviewIcon}
            actions={[
              review.canInsert && (
                <Link
                  key="updateReviewLink"
                  variant="button-subtle"
                  to={`/reviews/${review.id}/edit`}
                  id="updateReviewLink"
                >
                  {t("reviews.update")}
                </Link>
              ),
              <Link
                key="updateTemplateLink"
                variant="button"
                to={`/templates/${review.template.id}/edit?reviewId=${review.id}`}
                id="updateTemplateLink"
              >
                {t("reports.updateTemplate")}
              </Link>,

              (review.canDestroy || review.canInsert) && (
                <Menu key="reviewContextMenu">
                  <MenuButton
                    id="reviewMenuTrigger"
                    icon={DotsVerticalIcon}
                    variant="ghost"
                  />
                  <MenuList>
                    {review.canInsert && (
                      <MenuItem
                        data-test-id="sendReportNowMenuButton"
                        onSelect={() => setCreateReportModalOpen(true)}
                      >
                        {t("reports.sendNow")}
                      </MenuItem>
                    )}
                    {review.canDestroy && (
                      <MenuItem
                        onSelect={handleDeleteReview}
                        data-test-id="deleteReviewMenuButton"
                      >
                        {t("forms.actions.delete")}
                      </MenuItem>
                    )}
                  </MenuList>
                </Menu>
              ),
            ]}
          />
          {deleteLoading ? (
            <div className="flex justify-center flex-grow py-5">
              <TailSpin className="w-10" />
            </div>
          ) : (
            <Container className="space-y-8">
              <UserFollowUpOverdueAlert review={review} />
              <UserFollowUpStatus review={review} />
              <Tabs index={tab} onChange={handleTabChange}>
                <TabList>
                  <Tab>{t("reviews.summary")}</Tab>
                  <Tab>{t("reviews.skillsAnalytics")}</Tab>
                  <Tab>{t("reviews.evaluationsAnalytics")}</Tab>
                  <Tab>{t("reviews.reportsTab")}</Tab>
                  <Tab>{t("reviews.details")}</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel className="bg-white rounded-sm">
                    <UserFollowUpBasicInformations review={review} />
                  </TabPanel>
                  <TabPanel className="bg-white rounded-sm">
                    <UserFollowUpAnalytics
                      review={review}
                      type="QuestionSkill"
                      options={normalizedSkillptions}
                      emptyAnalyticsLabel={
                        t("reviews.noSkillsTracked", {
                          count: skillQuestionsCount,
                        }) as string
                      }
                    />
                  </TabPanel>
                  <TabPanel className="bg-white rounded-sm">
                    <UserFollowUpAnalytics
                      review={review}
                      type="QuestionEvaluation"
                      options={normalizedEvaluationOptions}
                      emptyAnalyticsLabel={
                        t("reviews.noEvaluationsTracked", {
                          count: evaluationQuestionsCount,
                        }) as string
                      }
                    />
                  </TabPanel>
                  <TabPanel className="bg-white rounded-sm">
                    <div className="space-y-8">
                      <UserFollowUpReports review={review} />
                    </div>
                  </TabPanel>
                  <TabPanel className="bg-white rounded-sm">
                    <UserFollowUpActivities review={review} />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Container>
          )}
          <CreateReportModal
            open={createReportModalOpen}
            onClose={() => setCreateReportModalOpen(false)}
            reviewId={review.id}
          />
        </>
      )}
    </ScreenContainer>
  );
};

export default UserFollowUpScreen;
